export type BrandingCapabilities = Readonly<{
  showKitopiLogo: boolean;
  showAcquisition: boolean;
  showRewards: boolean;
  showCashbackUI: boolean;
  showAppLinks: boolean;
  showAppPromptModal: boolean;
  showMenuAddToCart: boolean;
}>;

const parseWhitelabelDomains = (raw: string | undefined): string[] => {
  if (!raw) {
    return [];
  }

  return raw
    .split(',')
    .map(entry => entry.trim().toLowerCase())
    .filter(entry => entry.length > 0);
};

const matchesWhitelabel = (hostname: string, host: string, rawEnv: string | undefined): boolean => {
  const domains = parseWhitelabelDomains(rawEnv);
  if (domains.length === 0) {
    return false;
  }

  const normalizedHostname = hostname.trim().toLowerCase();
  const normalizedHost = host.trim().toLowerCase();

  return domains.includes(normalizedHostname) || domains.includes(normalizedHost);
};

export const deriveBranding = (
  hostname: string,
  host: string,
  rawEnv: string | undefined
): { isWhitelabel: boolean; capabilities: BrandingCapabilities } => {
  const isWhitelabel = matchesWhitelabel(hostname, host, rawEnv);

  return {
    isWhitelabel,
    capabilities: Object.freeze({
      showKitopiLogo: !isWhitelabel,
      showAcquisition: !isWhitelabel,
      showRewards: !isWhitelabel,
      showCashbackUI: !isWhitelabel,
      showAppLinks: !isWhitelabel,
      showAppPromptModal: !isWhitelabel,
      showMenuAddToCart: !isWhitelabel,
    }),
  };
};

const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '';
const currentHost = typeof window !== 'undefined' ? window.location.host : '';

const derived = deriveBranding(
  currentHostname,
  currentHost,
  import.meta.env.VITE_WHITELABEL_DOMAINS
);

export const isWhitelabel = derived.isWhitelabel;

/**
 * The domain the app is currently served from (e.g. `pay.kitopi.com` on the
 * Kitopi-branded site, or the partner host in a whitelabel deployment). Used to
 * render the live domain in the footer terms line and the legal documents
 * instead of a hardcoded `pay.kitopi.com`.
 */
export const currentDomain = currentHostname;

export const branding: BrandingCapabilities = derived.capabilities;

export const useBranding = (): BrandingCapabilities => branding;
